import { useCallback, useEffect, useRef, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';

import {
  leaveRoom,
  pollRoomChat,
  sendRoomChatMessage,
} from 'mastodon/actions/rooms';
import type {
  ApiRoomJSON,
  ApiRoomChatMessageJSON,
} from 'mastodon/api_types/rooms';
import { me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { useRoomAudioContext } from '../contexts/room_audio_context';

import { RoomChat } from './room_chat';

interface PlayerProps {
  layout?: 'mobile' | 'single-column' | 'multi-column';
}

export const RoomMiniPlayer: React.FC<PlayerProps> = ({
  layout = 'single-column',
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const { activeRoom, chatMessages } = useAppSelector(
    (state) =>
      (
        state as unknown as {
          rooms: {
            activeRoom: ApiRoomJSON | null;
            chatMessages: ApiRoomChatMessageJSON[];
          };
        }
      ).rooms,
  );

  const audioCtx = useRoomAudioContext();

  const isOnRoomsPage =
    location.pathname === '/rooms' || location.pathname.startsWith('/rooms/');
  if (!activeRoom || isOnRoomsPage || !audioCtx) return null;

  return (
    <RoomMiniPlayerInner
      room={activeRoom}
      chatMessages={chatMessages}
      audioCtx={audioCtx}
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed(!collapsed)}
      dispatch={dispatch}
      isMobile={layout === 'mobile'}
    />
  );
};

interface InnerProps {
  room: ApiRoomJSON;
  chatMessages: ApiRoomChatMessageJSON[];
  audioCtx: NonNullable<ReturnType<typeof useRoomAudioContext>>;
  collapsed: boolean;
  onToggleCollapse: () => void;
  dispatch: ReturnType<typeof useAppDispatch>;
  isMobile: boolean;
}

const RoomMiniPlayerInner: React.FC<InnerProps> = ({
  room,
  chatMessages,
  audioCtx,
  collapsed,
  onToggleCollapse,
  dispatch,
  isMobile,
}) => {
  const { isMuted, forceMuted, toggleMute, speakingPeers, cleanup } = audioCtx;

  const chatMessagesRef = useRef(chatMessages);
  chatMessagesRef.current = chatMessages;

  useEffect(() => {
    const interval = setInterval(() => {
      const msgs = chatMessagesRef.current;
      const sinceId = msgs.length > 0 ? msgs[msgs.length - 1]?.id : undefined;
      void dispatch(pollRoomChat(room.id, sinceId));
    }, 3000);
    return () => clearInterval(interval);
  }, [dispatch, room.id]);

  const handleSendChat = useCallback(
    (text: string, mediaAttachmentId?: string, replyToId?: string) => {
      void dispatch(
        sendRoomChatMessage(room.id, text, mediaAttachmentId, replyToId),
      );
    },
    [dispatch, room.id],
  );

  const handleLeave = useCallback(() => {
    cleanup();
    void dispatch(leaveRoom(room.id));
  }, [dispatch, room.id, cleanup]);

  const handleCollapseClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onToggleCollapse();
    },
    [onToggleCollapse],
  );

  const handleMuteClick = useCallback(() => {
    // If the host has force-muted us, ignore the click — the server revoked
    // our publish permission, so self-unmute is a no-op anyway.
    if (forceMuted) return;
    toggleMute();
  }, [forceMuted, toggleMute]);

  const containerClass = [
    'room-mini-player',
    collapsed ? 'room-mini-player--collapsed' : '',
    isMobile ? 'room-mini-player--mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const muteBtnClass = [
    'room-mini-player__mute-btn',
    isMuted ? 'is-muted' : '',
    forceMuted ? 'room-mini-player__mute-btn--locked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const muteTitle = forceMuted ? 'Muted by host' : isMuted ? 'Unmute' : 'Mute';

  return (
    <div className={containerClass}>
      <Link to='/rooms' className='room-mini-player__header'>
        <div className='room-mini-player__title'>
          <span className='room-mini-player__live-dot' />
          {room.title}
        </div>
        <div className='room-mini-player__header-actions'>
          <span className='room-mini-player__count'>
            {room.participant_count} listening
          </span>
          <button
            className='room-mini-player__collapse-btn'
            onClick={handleCollapseClick}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? '▲' : '▼'}
          </button>
        </div>
      </Link>

      {!collapsed && (
        <>
          {!isMobile && (
            <div className='room-mini-player__participants'>
              {room.participants.slice(0, 8).map((p) => (
                <div
                  key={p.id}
                  className={`room-mini-player__participant ${
                    speakingPeers.has(p.account.id)
                      ? 'room-mini-player__participant--speaking'
                      : ''
                  }`}
                  title={p.account.display_name || p.account.username}
                >
                  <img
                    src={p.account.avatar}
                    alt={p.account.display_name || p.account.username}
                  />
                </div>
              ))}
            </div>
          )}

          {!isMobile && (
            <RoomChat
              messages={chatMessages}
              onSend={handleSendChat}
              participants={room.participants}
              currentAccountId={me ?? undefined}
              compact
              canPostMedia={room.participants.some(
                (p) =>
                  p.account.id === me &&
                  (p.role === 'host' ||
                    p.role === 'cohost' ||
                    p.role === 'speaker'),
              )}
            />
          )}

          <div className='room-mini-player__controls'>
            <button
              className={muteBtnClass}
              onClick={handleMuteClick}
              disabled={forceMuted}
              title={muteTitle}
              aria-label={muteTitle}
            >
              {forceMuted ? '🔒' : isMuted ? '🔇' : '🎤'}
            </button>
            <button
              className='room-mini-player__leave-btn'
              onClick={handleLeave}
            >
              {isMobile ? 'Leave' : 'Leave Room'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
